import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { NavLink, useNavigate, useSearchParams } from 'react-router';
import { Form, Formik } from 'formik';

import { clearAuthSession } from 'api/clearAuthSession';
import { AuthPrivacyConsent } from 'components/AuthPrivacyConsent/AuthPrivacyConsent';
import { PasswordRulesHint } from 'components/PasswordRulesHint';
import { fetchAuthSession, isAuthSessionActive } from 'hooks/useAuthSession';
import {
  useRegisterFlow,
  type RegisterFlowRole,
  type RegisterFlowValues,
} from 'hooks/useRegisterFlow';
import { useLoginMutation } from 'modules/Login/services';
import { OtpCodeScreen } from 'modules/Otp/OtpCodeScreen';
import {
  clearOtpScreen,
  loadOtpScreen,
  otpScreenSecondsLeft,
  patchOtpScreen,
  saveOtpScreen,
} from 'utils/otpScreenStorage';
import { useStartOtp } from 'hooks/useOtpChallenge';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';
import { addNotification } from 'notifications';
import { Button, Select, TextField } from 'ui';
import { completeOtpAuthSession, enterCabinetAfterRegistration } from 'utils/completeOtpAuthSession';
import { consumeReturnTo } from 'utils/returnTo';

import classes from './register.module.scss';
import { registrationSchema } from './schema';
import { METRIKA_GOALS, reachMetrikaGoal } from 'utils/metrikaGoals';
import { captureReturnTo, withReturnToQuery } from 'utils/returnTo';
import { readPodborDraft } from 'utils/podborDraft';

const SELECT_OPTION_PROPS_MAP = {
  key: 'value' as const,
  title: 'label' as const,
  viewValue: 'label' as const,
};

const GENDER_OPTIONS = [
  { value: 'male', label: 'Мужской' },
  { value: 'female', label: 'Женский' },
];

const ROLE_OPTIONS = [
  { value: 'student', label: 'Ученик или родитель' },
  { value: 'teacher', label: 'Репетитор' },
];

interface PasswordConfirmState {
  existingRoles: RegisterFlowRole[];
  message: string;
  pendingValues: RegisterFlowValues;
}

interface OtpState {
  purpose: 'register' | 'login';
  phone: string;
  challengeId?: string;
  channel?: string;
  cascadeAfter?: number;
  hasPassword?: boolean;
  error?: string;
}

export const Registration: FC = () => {
  const { mutate: registerFlow, isPending } = useRegisterFlow();
  const loginApi = useLoginMutation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = captureReturnTo(searchParams);
  const roleParam = (searchParams.get('role') || '').toLowerCase();
  const initialRole =
    roleParam === 'teacher' || roleParam === 'tutor' ? 'teacher' : 'student';
  const [passwordConfirm, setPasswordConfirm] = useState<PasswordConfirmState | null>(null);
  const [existingPassword, setExistingPassword] = useState('');
  // F5 на экране кода (Figma 21:10034): тот же challenge из sessionStorage, без повторной отправки.
  const [restoredOtp] = useState(() => loadOtpScreen('registration'));
  const [otpState, setOtpState] = useState<OtpState | null>(() =>
    restoredOtp
      ? {
          purpose: restoredOtp.purpose,
          phone: restoredOtp.phone,
          challengeId: restoredOtp.challengeId,
          channel: restoredOtp.channel,
          cascadeAfter: otpScreenSecondsLeft(restoredOtp),
          hasPassword: restoredOtp.hasPassword,
          error: restoredOtp.error,
        }
      : null,
  );
  const [phoneLoginMode, setPhoneLoginMode] = useState<'choice' | 'otp' | 'password'>(
    restoredOtp ? 'otp' : 'choice',
  );
  const [phoneLoginPassword, setPhoneLoginPassword] = useState('');
  const [otpGateError, setOtpGateError] = useState('');
  const startOtp = useStartOtp(true);

  useEffect(() => {
    reachMetrikaGoal(METRIKA_GOALS.registrationPage);
    const controller = new AbortController();
    void (async () => {
      const session = await fetchAuthSession();
      if (controller.signal.aborted || isAuthSessionActive(session)) return;
      await clearAuthSession(controller.signal);
    })();
    return () => controller.abort();
  }, []);

  const cookieRegistrant = readPodborDraft()?.registrantType;
  // Переход со входа/восстановления: «Аккаунт не найден» -> «Зарегистрироваться» переносит номер или почту.
  const emailParam = (searchParams.get('email') || '').includes('@') ? searchParams.get('email') || '' : '';
  const initialValues = {
    method: (emailParam && !restoredOtp ? 'email' : 'phone') as 'email' | 'phone',
    email: emailParam,
    phone: restoredOtp?.phone || searchParams.get('phone') || '',
    password: '',
    confirmPassword: '',
    role: initialRole,
    studentRegistrantType:
      cookieRegistrant === 'parent' || cookieRegistrant === 'self' ? cookieRegistrant : 'self',
    name: '',
    surname: '',
    patronymic: '',
    // Figma 21:8834: поле «Пол» пустое с подсказкой «Выберите пол»; обязательность - в schema.ts.
    gender: '',
    childName: '',
    childGender: '',
    birthDate: '',
    privacyConsent: false,
  };

  const handleSubmit = (values: typeof initialValues) => {
    const { confirmPassword: _confirmPassword, privacyConsent: _privacyConsent, ...registerValues } =
      values;

    if (passwordConfirm) {
      registerFlow(
        { ...passwordConfirm.pendingValues, password: existingPassword },
        {
          onSuccess: (data) => {
            if (!data?.requires_password_confirmation) {
              setPasswordConfirm(null);
              setExistingPassword('');
            }
          },
        },
      );
      return;
    }

    registerFlow(registerValues, {
      onSuccess: (data) => {
        if (data?.requires_password_confirmation) {
          setPasswordConfirm({
            existingRoles: data.existing_roles ?? [],
            message:
              data.message ??
              'Аккаунт с этим email уже существует. Введите существующий пароль, чтобы добавить вторую роль.',
            pendingValues: registerValues,
          });
          setExistingPassword('');
        }
        if (data?.phone_otp_required) {
          setOtpState({
            purpose: 'register',
            phone: data.phone || registerValues.phone || '',
            challengeId: data.challenge_id || '',
            channel: data.channel || 'sms',
            cascadeAfter: data.cascade_after,
            error: data.otp_error || '',
          });
          saveOtpScreen({
            flow: 'registration',
            purpose: 'register',
            phone: data.phone || registerValues.phone || '',
            challengeId: data.challenge_id || '',
            channel: data.channel || 'sms',
            cascadeAfter: data.cascade_after,
            error: data.otp_error || '',
          });
          setPhoneLoginMode('otp');
        }
        if (data?.phone_login_required) {
          setOtpState({
            purpose: 'login',
            phone: data.phone || registerValues.phone || '',
            hasPassword: data.has_password,
          });
          setPhoneLoginMode('choice');
          setPhoneLoginPassword('');
        }
      },
    });
  };

  const handleCancelPasswordConfirm = () => {
    setPasswordConfirm(null);
    setExistingPassword('');
  };

  const existingRoleNames =
    passwordConfirm?.existingRoles.map((role) => role.title).join(', ') || '';

  return (
    <section className={classes.wrapper}>
      <main className={classes.container}>
        <Formik
          initialValues={initialValues}
          validationSchema={registrationSchema}
          onSubmit={handleSubmit}
          validateOnChange={true}
          validateOnBlur={true}
        >
          {({ values, errors, touched, handleBlur, setFieldValue }) => {
            const miss = (
              key:
                | 'email'
                | 'phone'
                | 'password'
                | 'confirmPassword'
                | 'name'
                | 'surname'
                | 'birthDate',
            ) => (touched[key] && errors[key] ? String(errors[key]) : undefined);
            // Figma 21:10034 / 21:4837: код - отдельный экран вместо формы (Formik остается, данные формы целы).
            if (
              otpState &&
              values.method === 'phone' &&
              !passwordConfirm &&
              (otpState.purpose === 'register' || phoneLoginMode === 'otp')
            ) {
              const purpose = otpState.purpose;
              return (
                <OtpCodeScreen
                  purpose={purpose}
                  phone={otpState.phone}
                  challengeId={otpState.challengeId || ''}
                  channel={otpState.channel}
                  cascadeAfter={otpState.cascadeAfter}
                  initialError={otpState.error}
                  onChallengeChange={(next) => patchOtpScreen('registration', next)}
                  onChangeNumber={() => {
                    clearOtpScreen();
                    setOtpState(null);
                    setPhoneLoginMode('choice');
                  }}
                  onVerified={(data) => {
                    clearOtpScreen();
                    if (purpose === 'login') {
                      completeOtpAuthSession(data, navigate, consumeReturnTo());
                      return;
                    }
                    enterCabinetAfterRegistration(data, navigate);
                  }}
                />
              );
            }

            return (
            <Form className={classes.form}>
              <div className={classes.heading}>
                <h1 className={classes.title}>
                  {passwordConfirm ? 'Подтверждение пароля' : 'Регистрация'}
                </h1>
                {passwordConfirm ? null : (
                  <p className={classes.subtitle}>
                    {values.role === 'teacher'
                      ? 'Создайте аккаунт репетитора.'
                      : 'Создайте аккаунт для выбора репетитора и занятий.'}
                  </p>
                )}
              </div>

              {passwordConfirm ? (
                <div className={classes.passwordConfirmBlock}>
                  <div className={classes.infoBox}>
                    <p className={classes.infoBoxText}>{passwordConfirm.message}</p>
                    {existingRoleNames && (
                      <p className={classes.infoBoxTextWithMargin}>
                        Текущие роли: <strong>{existingRoleNames}</strong>
                      </p>
                    )}
                  </div>

                  <div className={classes.fieldGroup}>
                    <TextField
                      shape='brick'
                      label='Пароль от существующего аккаунта'
                      placeholder='Пароль'
                      size='lg'
                      type='password'
                      name='existingPassword'
                      className='fig-auth-field'
                      value={existingPassword}
                      onChange={(val) => setExistingPassword(val)}
                    />
                  </div>

                  <Button
                    type='submit'
                    variant='filled'
                    shape='brick'
                    color='primary'
                    widthFullSize
                    loading={isPending}
                    disabled={isPending || !existingPassword}
                    className={classes.submitButton}
                  >
                    Добавить роль
                  </Button>

                  <Button
                    type='button'
                    variant='clear'
                    color='primary'
                    size='clear'
                    className={classes.passwordConfirmBack}
                    onClick={handleCancelPasswordConfirm}
                  >
                    Вернуться к регистрации
                  </Button>
                </div>
              ) : (
                <>
                  {/* БЛОК АВТОРИЗАЦИИ */}
                  <div className={classes.fieldGroup}>
                    <fieldset className={classes.radioGroupBlock}>
                      <legend className={classes.radioLabel}>Способ входа</legend>
                      <div className={classes.radioGroup} role='radiogroup'>
                        <label className={classes.radioItem}>
                          <input
                            type='radio'
                            name='method'
                            value='phone'
                            checked={values.method === 'phone'}
                            onChange={() => {
                              clearOtpScreen();
                              setOtpState(null);
                              setFieldValue('method', 'phone');
                            }}
                          />
                          <span>Телефон</span>
                        </label>
                        <label className={classes.radioItem}>
                          <input
                            type='radio'
                            name='method'
                            value='email'
                            checked={values.method === 'email'}
                            onChange={() => {
                              clearOtpScreen();
                              setOtpState(null);
                              setFieldValue('method', 'email');
                            }}
                          />
                          <span>Почта</span>
                        </label>
                      </div>
                    </fieldset>

                    {values.method === 'email' ? (
                      <>
                        <TextField
                          shape='brick'
                          label='Почта'
                          placeholder='name@example.com'
                          size='lg'
                          name='email'
                          className='fig-auth-field'
                          value={values.email}
                          onChange={(val) => setFieldValue('email', val)}
                          onBlur={handleBlur}
                          isInvalid={miss('email')}
                        />

                        <TextField
                          shape='brick'
                          label='Пароль'
                          placeholder='Пароль'
                          size='lg'
                          type='password'
                          name='password'
                          className='fig-auth-field'
                          value={values.password}
                          onChange={(val) => setFieldValue('password', val)}
                          onBlur={handleBlur}
                          isInvalid={miss('password')}
                        />
                        <PasswordRulesHint />

                        <TextField
                          shape='brick'
                          label='Повторите пароль'
                          placeholder='Пароль'
                          size='lg'
                          type='password'
                          name='confirmPassword'
                          className='fig-auth-field'
                          value={values.confirmPassword}
                          onChange={(val) => setFieldValue('confirmPassword', val)}
                          onBlur={handleBlur}
                          isInvalid={miss('confirmPassword')}
                        />
                      </>
                    ) : otpState?.purpose === 'login' && phoneLoginMode !== 'otp' ? (
                      <div className={classes.passwordConfirmBlock}>
                        <p className={classes.infoBoxText}>
                          Этот телефон уже зарегистрирован. Код регистрации не отправлен - войдите по
                          коду или паролю.
                        </p>
                        <NavLink
                          to={withReturnToQuery(
                            `/login?phone=${encodeURIComponent(otpState.phone)}`,
                            returnTo,
                          )}
                          className={classes.link}
                        >
                          Войти
                        </NavLink>
                        {otpGateError ? (
                          <p className={classes.errorText} role='alert'>
                            {otpGateError}
                          </p>
                        ) : null}
                        <div className={classes.radioGroup}>
                          <Button
                            type='button'
                            variant={phoneLoginMode === 'choice' ? 'filled' : 'outlined'}
                            shape='brick'
                            loading={startOtp.isPending}
                            disabled={startOtp.isPending}
                            onClick={async () => {
                              setOtpGateError('');
                              try {
                                const data = await startOtp.mutateAsync({
                                  purpose: 'login',
                                  phone: otpState.phone,
                                });
                                if (data.stub) {
                                  const msg =
                                    'Код не отправлен провайдером. Проверьте настройки SMS или попробуйте позже.';
                                  setOtpGateError(msg);
                                  addNotification(msg, 'error');
                                  return;
                                }
                                setOtpState((prev) =>
                                  prev
                                    ? {
                                        ...prev,
                                        challengeId: data.challenge_id,
                                        channel: data.channel,
                                        cascadeAfter: data.cascade_after,
                                      }
                                    : prev,
                                );
                                saveOtpScreen({
                                  flow: 'registration',
                                  purpose: 'login',
                                  phone: otpState.phone,
                                  hasPassword: otpState.hasPassword,
                                  challengeId: data.challenge_id,
                                  channel: data.channel,
                                  cascadeAfter: data.cascade_after,
                                });
                                setPhoneLoginMode('otp');
                              } catch (error) {
                                const msg = getApiErrorMessage(
                                  error,
                                  'Не удалось отправить код',
                                );
                                setOtpGateError('');
                                setOtpState((prev) =>
                                  prev
                                    ? {
                                        ...prev,
                                        challengeId: '',
                                        channel: 'sms',
                                        error: msg,
                                      }
                                    : prev,
                                );
                                saveOtpScreen({
                                  flow: 'registration',
                                  purpose: 'login',
                                  phone: otpState.phone,
                                  hasPassword: otpState.hasPassword,
                                  challengeId: '',
                                  channel: 'sms',
                                  cascadeAfter: 0,
                                  error: msg,
                                });
                                setPhoneLoginMode('otp');
                              }
                            }}
                          >
                            Получить код
                          </Button>
                          <Button
                            type='button'
                            variant='outlined'
                            shape='brick'
                            onClick={() => setPhoneLoginMode('password')}
                          >
                            Ввести пароль
                          </Button>
                        </div>
                        {phoneLoginMode === 'password' ? (
                          <>
                            {otpState.hasPassword === false ? (
                              <p className={classes.errorText}>
                                Пароль еще не задан, войдите по коду или задайте его в профиле.
                              </p>
                            ) : (
                              <>
                                <TextField
                                  shape='brick'
                                  label='Пароль'
                                  type='password'
                                  size='lg'
                                  className='fig-auth-field'
                                  value={phoneLoginPassword}
                                  onChange={setPhoneLoginPassword}
                                />
                                <Button
                                  type='button'
                                  variant='filled'
                                  shape='brick'
                                  widthFullSize
                                  loading={loginApi.isPending}
                                  disabled={loginApi.isPending || phoneLoginPassword.length < 8}
                                  onClick={() =>
                                    loginApi.mutate({
                                      phone: otpState.phone,
                                      password: phoneLoginPassword,
                                    })
                                  }
                                >
                                  Войти
                                </Button>
                              </>
                            )}
                          </>
                        ) : null}
                      </div>
                    ) : (
                      <>
                        <TextField
                          shape='brick'
                          label='Телефон'
                          placeholder='+7 (___) ___-__-__'
                          size='lg'
                          name='phone'
                          mask='ru-phone'
                          className='fig-auth-field'
                          value={values.phone}
                          onChange={(val) => setFieldValue('phone', val)}
                          onBlur={handleBlur}
                          isInvalid={miss('phone')}
                        />
                      </>
                    )}
                  </div>

                  {!otpState ? (
                  <>
                  {/* РОЛЬ - одно поле, не отдельные CTA */}
                  <div className={classes.roleField}>
                    <Select
                      className='fig-auth-field'
                      shape='brick'
                      size='lg'
                      variant='outlined'
                      widthFullSize
                      label='Роль'
                      placeholder='Выберите'
                      options={ROLE_OPTIONS}
                      optionPropsMap={SELECT_OPTION_PROPS_MAP}
                      value={values.role}
                      onChange={(value) => {
                        setFieldValue('role', (value as 'student' | 'teacher') || 'student');
                      }}
                      createPortal
                      isInvalid={!!(touched.role && errors.role)}
                    />
                    {touched.role && errors.role ? (
                      <div className={classes.errorText}>{errors.role}</div>
                    ) : null}
                  </div>

                  {values.role === 'student' && (
                    <fieldset className={classes.radioGroupBlock}>
                      <legend className={classes.radioLabel}>Кому нужен аккаунт</legend>
                      <div
                        className={`${classes.radioGroup} ${classes.radioGroupColumn}`}
                        role='radiogroup'
                      >
                        <label className={`${classes.radioItem} ${classes.radioItemWithHint}`}>
                          <input
                            type='radio'
                            name='studentRegistrantType'
                            value='self'
                            checked={values.studentRegistrantType === 'self'}
                            onChange={(e) => setFieldValue('studentRegistrantType', e.target.value)}
                          />
                          <span className={classes.radioItemText}>
                            <span>Сам</span>
                            <span className={classes.radioItemHint}>
                              Буду заниматься самостоятельно
                            </span>
                          </span>
                        </label>
                        <label className={`${classes.radioItem} ${classes.radioItemWithHint}`}>
                          <input
                            type='radio'
                            name='studentRegistrantType'
                            value='parent'
                            checked={values.studentRegistrantType === 'parent'}
                            onChange={(e) => setFieldValue('studentRegistrantType', e.target.value)}
                          />
                          <span className={classes.radioItemText}>
                            <span>Для ребенка</span>
                            <span className={classes.radioItemHint}>
                              Буду помогать с выбором и занятиями
                            </span>
                          </span>
                        </label>
                      </div>
                      {touched.studentRegistrantType && errors.studentRegistrantType && (
                        <div className={classes.errorText}>{errors.studentRegistrantType}</div>
                      )}
                    </fieldset>
                  )}

                  {/* ФИО */}
                  <div className={classes.fieldGroup}>
                    <TextField
                      shape='brick'
                      label={
                        values.role === 'student' && values.studentRegistrantType === 'parent'
                          ? 'Ваше имя (родителя)'
                          : 'Ваше имя'
                      }
                      placeholder='Ваше имя'
                      size='lg'
                      name='name'
                      className='fig-auth-field'
                      value={values.name}
                      onChange={(val) => setFieldValue('name', val)}
                      onBlur={handleBlur}
                      isInvalid={miss('name')}
                    />

                    {/* Фамилия, отчество и дата рождения репетитора - в анкете (Figma 21:9440). */}
                  </div>

                  {/* p5: полей ребёнка (имя/пол) нет - у родителя только «Ваше имя (родителя)» и контакт. */}

                  {/* ПОЛ - выпадающий список как в Figma 21:8834. p6: у родителя тоже - «Ваш пол» (пол аккаунта). */}
                  {(
                  <div className={classes.roleField}>
                    <Select
                      className='fig-auth-field'
                      shape='brick'
                      size='lg'
                      variant='outlined'
                      widthFullSize
                      label='Ваш пол'
                      placeholder='Выберите пол'
                      options={GENDER_OPTIONS}
                      optionPropsMap={SELECT_OPTION_PROPS_MAP}
                      value={values.gender}
                      onChange={(value) => setFieldValue('gender', (value as string) || '')}
                      createPortal
                      isInvalid={!!(touched.gender && errors.gender)}
                    />
                    {touched.gender && errors.gender ? (
                      <div className={classes.errorText}>{errors.gender}</div>
                    ) : null}
                  </div>
                  )}


                  <AuthPrivacyConsent
                    mode='checkbox'
                    checked={values.privacyConsent}
                    onChange={(checked) => setFieldValue('privacyConsent', checked)}
                    disabled={isPending}
                    isInvalid={!!(touched.privacyConsent && errors.privacyConsent)}
                    errorText={
                      touched.privacyConsent && errors.privacyConsent
                        ? String(errors.privacyConsent)
                        : undefined
                    }
                  />

                  <Button
                    type='submit'
                    variant='filled'
                    shape='brick'
                    color='primary'
                    widthFullSize
                    loading={isPending}
                    // Телефон: неактивна, пока номер не введен полностью (по текущему значению поля).
                    disabled={
                      isPending ||
                      (values.method === 'phone' && values.phone.replace(/\D/g, '').length < 11)
                    }
                    className={classes.submitButton}
                  >
                    Создать аккаунт
                  </Button>
                  </>
                  ) : null}
                </>
              )}

              <div className={classes.loginPrompt}>
                <span>Уже есть аккаунт? </span>
                <NavLink to={withReturnToQuery('/login', returnTo)} className={classes.link}>
                  Войти
                </NavLink>
              </div>
            </Form>
            );
          }}
        </Formik>
      </main>
    </section>
  );
};
